// noinspection CssInvalidPropertyValue

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { MdHistory, MdOutlineCode } from 'react-icons/md';
import { TbPencil } from 'react-icons/tb';
import { IoMdCheckmark, IoMdClose } from 'react-icons/io';
import { useSignals } from '@preact/signals-react/runtime';
import { useComputed, useSignalEffect } from '@preact/signals-react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { useAiComponentContext } from '@/features/editor/widgets/ai-component/context';
import { AiComponentPrompt } from '@/features/editor/widgets/ai-component/tab-prompt';
import { AiComponentTab } from '@/features/editor/widgets/ai-component/models';
import { AiComponentCode } from '@/features/editor/widgets/ai-component/tab-code';
import { AiComponentVisualEdit } from '@/features/editor/widgets/ai-component/tab-visual-edit';
import { PopConfirm } from '@/components/pop-confirm';
import { EditorContext } from '@/features/editor/context/editor-context';
import { getQueryParam } from '@/utils/browser';
import { DeviceType } from '@/utils/definitions';
import { TabSelect } from '@/components/tab-select';
import { Flexbox } from '@/components/flex';
import { CheckpointPopover } from '@/features/ai-agents/components/checkpoint-popover';
import {
  HistoryCheckpoint,
  useAiMetadataApi,
} from '@/features/ai-agents/shared/use-ai-metadata-api';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

const TABS = [`HTML`, `CSS`, `JS`];

export function AiComponentTabContainer() {
  useSignals();
  const {
    devicePreview: {
      editorState: { device },
    },
  } = useContext(EditorContext);
  const hasChanges = useAiComponentContext((ctx) => ctx.hasChanges);
  const firstSessionId = useAiComponentContext((ctx) => ctx.firstSessionId);
  const lastRunId = useAiComponentContext((ctx) => ctx.lastRunId);
  const hasVisualEditManifest = useAiComponentContext(
    (ctx) => ctx.hasVisualEditManifest,
  );
  const selectedTab = useAiComponentContext((ctx) => ctx.selectedTab);
  const selectTab = useAiComponentContext((ctx) => ctx.selectTab);
  const setBackToTab = useAiComponentContext((ctx) => ctx.setBackToTab);
  const selectedCodeTab = useAiComponentContext((ctx) => ctx.selectedCodeTab);
  const onSelectedCodeTab = useAiComponentContext(
    (ctx) => ctx.onSelectedCodeTab,
  );
  const isFullscreen = useAiComponentContext((ctx) => ctx.isFullScreen);
  const isComponentReady = useAiComponentContext((ctx) => ctx.isComponentReady);
  const discardChanges = useAiComponentContext((ctx) => ctx.discardChanges);
  const applyChanges = useAiComponentContext((ctx) => ctx.applyChanges);
  const discardLocalChanges = useAiComponentContext(
    (ctx) => ctx.discardLocalChanges,
  );
  const restoreCheckpoint = useAiComponentContext(
    (ctx) => ctx.restoreCheckpoint,
  );
  const saveLocalChanges = useAiComponentContext((ctx) => ctx.saveLocalChanges);
  const { restoreSessionCheckpoint } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );

  const isHistoryEnabled = useFeatureBit(FeatureBit.SHOW_AI_HISTORY);

  const tabs = useMemo(() => {
    if (getQueryParam(`debug-ai`) === `1`) {
      return [...TABS, `M`];
    }
    return TABS;
  }, []);

  const onBackToChat = () => {
    selectTab(AiComponentTab.PROMPT);
  };

  const discardChangesAsync = async () => {
    discardChanges();
    return Promise.resolve();
  };

  const discardLocalChangesAsync = async () => {
    discardLocalChanges();
    return Promise.resolve();
  };

  const wrapperClasses = useComputed(() => {
    const classes = [];
    if (isFullscreen.value) {
      classes.push(`fullscreen`);
    }
    if (device === `mobile` && selectedTab.value === AiComponentTab.EDIT) {
      classes.push(`mobile-visual-edit`);
    }
    return classes.join(` `);
  });

  const outOfChat = useComputed(
    () => selectedTab.value !== AiComponentTab.PROMPT,
  );

  const visualEditBtnState = useComputed(() => {
    const cls = [];
    if (selectedTab.value === AiComponentTab.EDIT) {
      cls.push(`selected`);
    }
    if (selectedTab.value === AiComponentTab.CODE) {
      cls.push(`light`);
    }
    return cls.join(` `) || ``;
  });

  useEffect(() => {
    const handler = (ev) => {
      if (ev.data === `#edit-visual`) {
        selectTab(AiComponentTab.EDIT);
      } else if (ev.data === `#edit-code`) {
        selectTab(AiComponentTab.CODE);
      } else if (ev.data?.includes(`#edit-strategy-`)) {
        selectTab(AiComponentTab.EDIT);
        setBackToTab(AiComponentTab.PROMPT);
        setTimeout(() => {
          const buttonId = ev.data.replace(`#edit-strategy-`, `edit-strategy-`);
          const btn = document.getElementById(buttonId);
          btn?.click?.();
        }, 500);
      }
    };

    window?.addEventListener?.(`message`, handler);

    return () => window?.removeEventListener?.(`message`, handler);
  }, []);

  useEffect(() => {
    const lastTab = sessionStorage.getItem(`selectedTab`);
    if (lastTab) {
      selectTab(lastTab as AiComponentTab);
    }
    const lastCodeTab = sessionStorage.getItem(`selectedCodeTab`);
    if (lastCodeTab) {
      onSelectedCodeTab(lastCodeTab as 'HTML' | 'CSS' | 'JS');
    }
    return () => {
      sessionStorage.removeItem(`selectedTab`);
      sessionStorage.removeItem(`selectedCodeTab`);
    };
  }, []);

  useSignalEffect(() => {
    sessionStorage.setItem(`selectedTab`, selectedTab.value);
    sessionStorage.setItem(`selectedCodeTab`, selectedCodeTab.value);
  });

  const [isCheckpointOpen, setIsCheckpointOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const onRestoreCheckpoint = async (checkpoint: HistoryCheckpoint) => {
    if (checkpoint) {
      await restoreSessionCheckpoint(checkpoint.new_session_id, {
        id: `widget-developer`,
        type: `agent`,
      });
      restoreCheckpoint(checkpoint);
    }
  };

  return (
    <Wrapper ref={wrapperRef} className={wrapperClasses.value}>
      <HeaderWrapper>
        <HeaderRow>
          <VisualEditButton
            disabled={!isComponentReady.value || !hasVisualEditManifest.value}
            onClick={() => selectTab(AiComponentTab.EDIT)}
            className={visualEditBtnState.value}
          >
            <TbPencil size={13} />
            Design
          </VisualEditButton>
          <AiChatTabButton
            disabled={!isComponentReady.value}
            onClick={() => selectTab(AiComponentTab.CODE)}
            className={
              selectedTab.value === AiComponentTab.CODE ? `selected` : ``
            }
          >
            <MdOutlineCode size={16} />
            Code
          </AiChatTabButton>
          <Popover
            onClickOutside={() => setIsCheckpointOpen((prev) => !prev)}
            positions={[`bottom`, `left`]}
            isOpen={isCheckpointOpen}
            containerStyle={{ zIndex: `10000` }}
            boundaryElement={wrapperRef.current}
            content={({ position, childRect, popoverRect }) => (
              <ArrowContainer
                position={position}
                childRect={childRect}
                popoverRect={popoverRect}
                arrowColor="white"
                arrowStyle={{
                  top: `0.1rem`,
                  zIndex: `9999`,
                  borderBottomColor: `rgba(222, 222, 222, 0.8)`,
                  filter: `drop-shadow(0px 1px 6px rgba(0, 0, 0, 0.1))`,
                }}
                arrowClassName="change-target-arrow"
                arrowSize={10}
              >
                <CheckpointPopover
                  firstSessionId={firstSessionId}
                  lastRunId={lastRunId}
                  onRestoreSession={onRestoreCheckpoint}
                />
              </ArrowContainer>
            )}
          >
            <HistoryButton
              style={{
                opacity: isHistoryEnabled ? `1` : `0`,
                pointerEvents: isHistoryEnabled ? `auto` : `none`,
              }}
              className={isCheckpointOpen ? `open` : ``}
              onClick={() => setIsCheckpointOpen((prev) => !prev)}
            >
              <MdHistory
                size={24}
                color={isCheckpointOpen ? `#fff` : `#9daeb2`}
                className={`history clickable ${
                  isComponentReady.value ? `` : `disabled`
                }`}
              />
            </HistoryButton>
          </Popover>
          <div />
          <HeaderRightSection>
            {selectedTab.value === AiComponentTab.CODE &&
              (isFullscreen.value || device === DeviceType.Mobile) && (
                <TabSelect
                  tabs={tabs}
                  defaultValue={selectedCodeTab.value}
                  onTabSelected={(_, val) =>
                    onSelectedCodeTab(val as `HTML` | `CSS` | `JS`)
                  }
                  tabFont="Inter"
                  tabWidth={10}
                  tabHeight={3.5}
                  tabPadding="0"
                  containerPadding={0}
                  tabRadius={3}
                  tabFontSize="1rem"
                />
              )}
            {outOfChat.value && (
              <BackToChat onClick={onBackToChat}>Back to chat</BackToChat>
            )}
            {!outOfChat.value && (
              <>
                {hasChanges.value ? (
                  <PopConfirm
                    text="Are you sure you want to close? changes made to this component will be discarded"
                    onConfirm={discardChangesAsync}
                  >
                    <HeaderButton>
                      <IoMdClose size={20} className="close-btn" />
                    </HeaderButton>
                  </PopConfirm>
                ) : (
                  <HeaderButton onClick={discardChanges}>
                    <IoMdClose size={20} className="close-btn" />
                  </HeaderButton>
                )}
                {isComponentReady.value && hasChanges.value && (
                  <HeaderButton className="long" onClick={applyChanges}>
                    <IoMdCheckmark
                      size={20}
                      color="black"
                      className="save-btn"
                    />
                    Update
                  </HeaderButton>
                )}
              </>
            )}
          </HeaderRightSection>
        </HeaderRow>
        {!isFullscreen.value &&
          selectedTab.value === AiComponentTab.CODE &&
          device === DeviceType.Desktop && (
            <Flexbox align="center" justify="center">
              <TabSelect
                tabs={tabs}
                defaultValue={selectedCodeTab.value}
                onTabSelected={(_, val) =>
                  onSelectedCodeTab(val as `HTML` | `CSS` | `JS`)
                }
                tabFont="Inter"
                tabWidth={13}
                tabHeight={3.5}
                tabPadding="0"
                containerPadding={0}
                tabRadius={3}
                tabFontSize="1rem"
              />
            </Flexbox>
          )}
      </HeaderWrapper>
      <PromptWrapper
        className={selectedTab.value === AiComponentTab.PROMPT ? `` : `hide`}
      >
        <AiComponentPrompt />
      </PromptWrapper>
      {selectedTab?.value === AiComponentTab.CODE && <AiComponentCode />}
      {selectedTab?.value === AiComponentTab.EDIT && <AiComponentVisualEdit />}
      {selectedTab?.value !== AiComponentTab.PROMPT && hasChanges?.value && (
        <FooterWrapper
          className={device === DeviceType.Desktop ? `desktop` : ``}
        >
          <FooterButton onClick={discardLocalChangesAsync} className="outline">
            Discard
          </FooterButton>
          <FooterButton onClick={saveLocalChanges}>Save</FooterButton>
        </FooterWrapper>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  padding: 2rem;
  border-radius: 1.5rem;
  background-color: #f9f9f9;

  display: flex;
  flex-direction: column;
  gap: 2rem;

  overflow: hidden;
  max-height: 100%;

  &.fullscreen {
    width: 100% !important;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background-color: white;
  }

  &.mobile-visual-edit {
    width: 100%;

    && .visual-edit-tab {
      width: 76%;
      margin: 0 auto;
    }
  }

  transition: width 1s ease-in-out;
`;

const HistoryButton = styled.button`
  background: rgb(249, 249, 249);
  border: none;
  outline: none;
  appearance: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  cursor: pointer;
  border-radius: 1rem;

  &&.open {
    background: rgb(39, 92, 243);
    box-shadow: 0 1px 6px 2px rgba(39, 92, 243, 0.2);
  }
`;

const PromptWrapper = styled.div`
  display: flex;
  max-height: 100%;
  height: 100%;
  overflow: hidden;

  &.hide {
    display: none;
  }
`;

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .tab-select-container {
    background-color: rgb(233, 233, 233);
  }

  .tab-select-selector {
    border-color: transparent;
    box-shadow: 0 8px 10px 0 rgba(105, 122, 231, 0.11);
  }

  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const HeaderRow = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: auto auto auto 1fr auto;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 1.2rem;
  align-items: center;
`;

const HeaderRightSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  .close-btn,
  .save-btn {
    color: #a7b1b9;
    cursor: pointer;
    transition: color 0.2s ease-in-out;
  }

  .close-btn {
    &:hover {
      color: rgb(221, 11, 11);
      cursor: pointer;
    }
    &:active {
      color: #a7b1b9;
    }
  }

  .save-btn {
    &:hover {
      color: rgb(17, 165, 56);
    }
    &:active {
      color: #a7b1b9;
    }
  }
`;

const BackToChat = styled.button`
  color: #798794;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  text-decoration: underline;
  letter-spacing: -0.55px;
  cursor: pointer;
`;

const AiChatTabButton = styled.button`
  appearance: none;
  outline: none;
  border: 2px solid #a6afb8;
  color: #798794;
  background-color: transparent;
  position: relative;
  border-radius: 3rem;
  padding: 0.6rem 1.8rem;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 1.2rem;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;

  cursor: pointer;
  user-select: none;

  height: 3.5rem;

  &:hover {
    color: rgb(255, 255, 255);
    border-color: rgb(0, 0, 0);
    background: black;
  }

  &:active {
    color: rgb(255, 255, 255);
    border-color: rgb(0, 0, 0);
    background: black;
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &&.selected {
    font-weight: bold;
    background-color: black;
    color: #fff;
    border-color: black;
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0),
      0 6px 7px 0 rgba(151, 210, 206, 0.22), 0 1px 2px 0 rgba(0, 74, 186, 0.5);
  }

  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;
`;

const HeaderButton = styled.button`
  cursor: pointer;
  appearance: none;
  height: 3rem;
  display: flex;
  flex-direction: row;
  gap: 0.4rem;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  border-radius: 50%;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0),
    0 6px 7px 0 rgba(151, 210, 206, 0.22), 0 0.5px 0.5px 0 rgba(0, 0, 0, 0.15);
  background-color: #fff;
  letter-spacing: -0.56px;
  color: #000;
  font-family: Inter, sans-serif;
  font-size: 1.2rem;
  text-transform: uppercase;
  font-weight: 600;

  &&.long {
    padding: 0 1.4rem;
    border-radius: 3rem;
    color: black;
    transition: color 0.2s ease-in-out;
  }

  &&.long:hover {
     {
      color: rgb(14, 115, 41);
    }
  }
`;

const VisualEditButton = styled(AiChatTabButton)`
  position: relative;
  z-index: 1;
  overflow: hidden;
  transition: background-position 4s ease, background 0.2s ease-in-out;
  &&.light {
    &&::before {
      border: 2px solid #a6afb8;
      background: transparent;
    }
  }

  &&:not(:disabled) {
    border: none;
  }

  &&:disabled {
    &&::before {
      display: none;
    }
  }

  &&.selected {
    &&::before {
      display: none;
    }

    &&:hover {
      background: black;
      color: #fff;
    }
  }

  &&::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 2px; /* Border thickness */
    border-radius: 3rem;
    background: linear-gradient(
      110deg,
      #0ebeff,
      #ffdd40,
      #ae63e4,
      #47cf73,
      #0ebeff,
      #ffdd40,
      #ae63e4,
      #47cf73,
      #0ebeff,
      #ffdd40,
      #ae63e4,
      #47cf73,
      #0ebeff,
      #ffdd40,
      #ae63e4,
      #47cf73
    );
    background-size: 800% 400%;
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: -1;
  }

  &&:hover {
    background: transparent;
    color: #798794;

    &&::before {
      animation: rainbow-border 8s linear infinite;
    }
  }

  @keyframes rainbow-border {
    0% {
      background-position: 0% 50%;
    }
    100% {
      background-position: 200% 50%;
    }
  }
`;

const FooterWrapper = styled.div`
  position: absolute;
  width: 75%;
  bottom: 2rem;
  left: 50%;
  padding: 1.4rem 2rem;
  border-radius: 2rem;
  transform: translateX(-50%);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.1);

  &&.desktop {
    width: 90%;
  }

  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1.5rem;

  animation: reveal-bottom-up 0.6s ease-in-out;
  opacity: 0;
  animation-fill-mode: forwards;

  @keyframes reveal-bottom-up {
    from {
      opacity: 0;
      transform: translate(-50%, 4rem);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
`;

const FooterButton = styled.button`
  cursor: pointer;
  outline: none;
  appearance: none;
  background: black;
  border: 1px solid black;
  color: white;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 1.4rem;
  padding: 1rem 3rem;
  border-radius: 1rem;
  width: 100%;

  &&.outline {
    background: transparent;
    color: black;
  }

  transition: opacity 0.2s ease-in-out;

  &&:hover {
    opacity: 0.8;
  }

  &&:active {
    opacity: 1;
  }
`;
